"use client";

import { useState, useCallback, useEffect } from "react";
import useBoolean from "./useBoolean";
import { settings } from "shared";

type Props = {
  itemRef: React.RefObject<HTMLElement>;
  onOpenMenu?: (e: React.MouseEvent<HTMLDivElement>) => boolean;
  onCloseMenu?: () => void;
};

/**
 * Hook for using context menus related to individual items
 * @param {Object} itemRef Ref for the item
 * @param {Function} onOpenMenu Function that runs when the menu is opened.
 */
export default function useContextMenu({ itemRef, onOpenMenu, onCloseMenu }: Props) {
  const [contextMenuXPos, setXPos] = useState("0px");
  const [contextMenuYPos, setYPos] = useState("0px");
  const [menuOpen, setMenuOpen, setMenuClosed] = useBoolean(false);

  const handleContextMenu = useCallback(
    (e: MouseEvent) => {
      /**
       * In development, its a hinderance to inspecting elements.
       * Hold the shift key while right clicking to inspect.
       */
      if (e.shiftKey && settings.environment !== "production") return;

      e.preventDefault();
      setXPos(`${e.pageX}px`);
      setYPos(`${e.pageY}px`);
      const shouldOpen = onOpenMenu?.(e as unknown as React.MouseEvent<HTMLDivElement>);
      if (shouldOpen !== false) setMenuOpen();
    },
    [onOpenMenu, setMenuOpen]
  );

  const handleContextMenuClose = useCallback(() => {
    if (menuOpen) {
      setMenuClosed();
      onCloseMenu?.();
    }
  }, [menuOpen, onCloseMenu, setMenuClosed]);

  useEffect(() => {
    const element = itemRef.current;
    element?.addEventListener("contextmenu", handleContextMenu);

    return () => {
      element?.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [itemRef, handleContextMenu]);

  return {
    contextMenuXPos,
    contextMenuYPos,
    contextMenuOpen: menuOpen,
    handleContextMenuClose,
    setMenuOpen,
    setMenuClosed,
  };
}
